import { useAppSelector } from 'modules/state-mgmt/store';
import React, { createContext, useContext, useState, ReactNode, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatAddress } from 'utils/addressUtils';
import { isValidMobileNumber } from 'utils';
import { useConsentFormFields } from '../hooks/useConsentFormFields';
import { FIELD_CODE } from '../types';
import { useWizardStepNavigation } from 'modules/views/shared/Wizard';

interface ConsentFormContextType {
  step: number;
  isLoading: boolean;
  setIsLoading: (v: boolean) => void;
  firstStep: () => void;
  nextStep: () => void;
  prevStep: () => void;
  projectId: string;
  projectName: string;
  projectAddress: string;
  companies: any[];
  consentFormFields: any[];
  formValues: any;
  updateValue: (companyId: string, fieldId: string, value: any, meta: any) => void;
  validateForm: () => boolean;
  consentFormLegal: {
    body: string;
    languageCode: string;
    languageId: string;
    id: string;
    name: string;
    htmlBody?: string;
  };
  languageCode: string;
  workerId: string;
  workerUserId: string;
  getFormPayloads: () => {
    companyId: string;
    formPayload: { consentFormFieldValueId: string | null; id: string; value: any }[];
  }[];
  assignedCompanies: { id: string; name: string; workerProjectStatus: number }[];
  isArchivedProject: boolean;
  addEmployerFlow: boolean;
  isWorker: boolean;
  signature: string;
}

enum WORKER_PROJECT_STATUS {
  NOT_ACCEPTED = 0,
  ACCEPTED = 1,
}

const ConsentFormContext = createContext<ConsentFormContextType | undefined>(undefined);

interface ConsentFormProviderProps {
  children: ReactNode;
}

export const ConsentFormProvider: React.FC<ConsentFormProviderProps> = ({ children }) => {
  const { t } = useTranslation();
  const { goToPreviousStep, canGoToPreviousStep } = useWizardStepNavigation();
  const {
    projectId,
    projectName,
    projectAddress,
    onboardingCompanies,
    socialSecurityNumber,
    consentFormLegals,
    languageCode,
    workerId,
    workerUserId,
    assignedCompanies,
    isArchivedProject,
    existingConsentFormForAllCompanies,
    addEmployerFlow,
    tempCompanies,
    isWorker,
    signature,
    isOfflineWorker,
    offlineConsentFormPayload,
  } = useAppSelector(state => ({
    projectId: state.cache.project.id,
    projectName: state.cache.project.name,
    projectAddress: formatAddress(state.cache.project.jobSiteAddress),
    onboardingCompanies:
      state.onboarding.temp?.employers?.map(employer => ({
        id: employer.value,
        name: employer.label,
      })) || [],
    socialSecurityNumber: state.onboarding.socialSecurityNumber,
    consentFormLegals: state.cache.project.consentFormLegals,
    languageCode: state.app?.languageCode,
    workerId: state.onboarding.id,
    workerUserId: state.onboarding.userId,
    assignedCompanies: state.onboarding.assignedCompanies || [],
    isArchivedProject: state.cache.project.status === 3,
    existingConsentFormForAllCompanies: state.onboarding.existingConsentFormForAllCompanies || [],
    addEmployerFlow: state.onboarding.temp?.addEmployerFlow || false,
    tempCompanies: state.onboarding.temp?.employers || [],
    isWorker: state.user?.isWorker,
    signature: state.onboarding?.temp?.signature,
    isOfflineWorker: state.onboarding?.uploadingDetails?.isOffline,
    offlineConsentFormPayload: state.onboarding?.temp?.consentFormPayloads,
  }));

  const { consentFormFields } = useConsentFormFields();
  const [step, setStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [formValues, setFormValues] = useState({});

  const companies = useMemo(() => {
    if (addEmployerFlow) {
      return assignedCompanies?.map(ac => ({ id: ac.id, name: ac.name })) || [];
    }
    return onboardingCompanies;
  }, [addEmployerFlow, onboardingCompanies, tempCompanies, assignedCompanies]);

  const consentFormLegal = useMemo(() => {
    return consentFormLegals?.find(legal => legal.languageCode === languageCode);
  }, [languageCode, consentFormLegals]);

  const getPreFilledValue = field => {
    switch (field.code) {
      case FIELD_CODE.LAST_4_SSN:
        return socialSecurityNumber || '';
      default:
        break;
    }
    return '';
  };

  useEffect(() => {
    const initialValues = {};
    companies.forEach(company => {
      let existingFormForCompany = {} as any;
      if (isOfflineWorker) {
        const consentForm = offlineConsentFormPayload?.find(form => form.companyId === company.id);
        if (consentForm) {
          existingFormForCompany = {
            company: {
              id: company.id,
              name: company.name,
            },
            consentFormFields: consentForm.formPayload,
          };
        }
      } else {
        existingFormForCompany = existingConsentFormForAllCompanies.find(form => form.company?.id === company.id);
      }
      const workerProjectStatus =
        assignedCompanies.find(ac => ac.id === company.id)?.workerProjectStatus || WORKER_PROJECT_STATUS.NOT_ACCEPTED;
      const data = {};
      consentFormFields?.map(field => {
        const existingField = existingFormForCompany?.consentFormFields?.find(f => f.id === field.id);
        data[field.id] = {
          error: '',
          isMandatory: field?.projectField?.isMandatory,
          value: existingField?.value || getPreFilledValue(field),
          disabled: workerProjectStatus == WORKER_PROJECT_STATUS.ACCEPTED,
        };
      });
      initialValues[company.id] = data;
    });
    setFormValues(initialValues);
  }, []);

  const updateValue = (companyId: string, fieldId: string, value: any, meta: any) => {
    setFormValues(prevValues => ({
      ...prevValues,
      [companyId]: {
        ...prevValues[companyId],
        [fieldId]: {
          ...prevValues[companyId][fieldId],
          value: value,
          error: '',
          meta: { ...(meta || {}) },
        },
      },
    }));
  };

  const getInputValueError = (field, value, meta) => {
    const fieldCode = field?.dataType === 3 ? FIELD_CODE.PHONE_DATA_TYPE : field?.code;
    switch (fieldCode) {
      case FIELD_CODE.LAST_4_SSN: {
        if (value || field?.projectField?.isMandatory) {
          return !/^\d{4}$/.test(value) ? t('consentForm.step2.errors.invalidSSN') : '';
        } else {
          return '';
        }
      }
      case FIELD_CODE.PHONE_DATA_TYPE: {
        if (meta?.inputValue?.split(' ')?.filter(Boolean)?.length >= 2 && !isValidMobileNumber(value)) {
          return t('consentForm.step2.errors.invalidPhoneNumber');
        }
        return '';
      }
      default:
        return '';
    }
  };

  const validateForm = () => {
    let isValid = true;
    const newFormValues = { ...formValues };

    companies.forEach(company => {
      consentFormFields.forEach(field => {
        const fieldData = newFormValues[company.id]?.[field.id];
        if (fieldData?.isMandatory) {
          const value = fieldData.value;
          const isEmpty =
            value === '' ||
            value === null ||
            value === undefined ||
            (field?.dataType === 3 && fieldData?.meta?.inputValue?.split(' ')?.filter(Boolean)?.length < 2);
          if (isEmpty && !fieldData?.disabled) {
            newFormValues[company.id][field.id] = {
              ...fieldData,
              error: t('common.fieldRequired', { field: field.name }),
            };
            isValid = false;
          }
        }
        if (!fieldData?.error) {
          const customError = getInputValueError(field, fieldData?.value, fieldData?.meta);
          if (!!customError) {
            newFormValues[company.id][field.id] = {
              ...fieldData,
              error: customError,
            };
            isValid = false;
          }
        }
      });
    });

    setFormValues(newFormValues);
    return isValid;
  };

  const getFormPayloads = () => {
    const payloads = [];
    Object.entries(formValues).map(([companyId, formaData]) => {
      const workerProjectStatus =
        assignedCompanies.find(ac => ac.id === companyId)?.workerProjectStatus || WORKER_PROJECT_STATUS.NOT_ACCEPTED;
      if (workerProjectStatus == WORKER_PROJECT_STATUS.NOT_ACCEPTED) {
        const formPayload = [];
        Object.entries(formaData).map(([fieldId, { value, meta }]) => {
          value &&
            (meta?.dataType === 3 ? isValidMobileNumber(value) : true) &&
            formPayload.push({
              consentFormFieldValueId: null,
              id: fieldId,
              value: value,
            });
        });
        payloads.push({ companyId, formPayload });
      }
    });
    return payloads;
  };

  const firstStep = () => {
    setStep(0);
  };

  const nextStep = () => {
    setStep(prevStep => {
      const newStep = Math.min(prevStep + 1, 2);
      return newStep;
    });
  };

  const prevStep = () => {
    if (step === 0) {
      canGoToPreviousStep && goToPreviousStep();
      return;
    }
    setStep(prevStep => {
      const newStep = Math.max(0, prevStep - 1);
      return newStep;
    });
  };

  return (
    <ConsentFormContext.Provider
      value={{
        step,
        isLoading,
        setIsLoading,
        firstStep,
        nextStep,
        prevStep,
        projectId,
        projectName,
        projectAddress,
        companies,
        consentFormFields,
        formValues,
        updateValue,
        validateForm,
        consentFormLegal,
        languageCode,
        getFormPayloads,
        workerId,
        workerUserId,
        assignedCompanies,
        isArchivedProject,
        addEmployerFlow,
        isWorker,
        signature,
      }}
    >
      {children}
    </ConsentFormContext.Provider>
  );
};

export const useConsentFormContext = () => {
  const context = useContext(ConsentFormContext);
  if (context === undefined) {
    throw new Error('useConsentFormContext must be used within a ConsentFormProvider');
  }
  return context;
};
